"use strict";
const InputBaseControl = require("@gluenetworks/input_base_control").Base;
const _ = require("lodash");
const Renderer = require("./DatePickerRenderer");
const moment = require("moment");
const Calendar = require("../lib/Calendar");

class DatePickerComponent extends InputBaseControl {
    /**
     * @schema.name Date Picker Control
     * @schema.package @gluenetworks/date_time_picker
     * @schema.packageIdentifier DatePicker
     * @schema.namespace require("@gluenetworks/steve_ui_core").Control.Timestamp.DatePicker
     * @schema.events ["Disabled", "Value", "Visible", "Invalid", "DarkTheme", "Theme", "Focus", "Blur", "SetAttribute", "UserValue", "UserFocus", "UserBlur"]
     *
     * @schema.configuration.view.name view
     * @schema.configuration.view.type string
     * @schema.configuration.view.default days
     * @schema.configuration.view.required false
     *
     * @schema.configuration.value.example { year: 1999, month: 1, date:1 }
     */

    constructor(config) {
        super(config);
        this._view = config && config.view ? config.view : "days";
        this._views = ["years", "days"];
        if (!(this._value instanceof moment)) {
            let origValue = this._value;
            this._value = moment();
            this._calendar = moment(this._value);
            this.setValue(origValue);
        } else {
            this._calendar = moment(this._value);
            this._setCalendarWeekDays();
        }
        if (config && config.view && this._views.includes(config.view)) {
            this._view = config.view;
        }
        this._renderer = new Renderer(this);
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/

    _setCalendarMetaData() {
        let selectedMonth = this._value.get("month");
        let selectedDate = this._value.get("date");
        _.each(this._calendarWeekDays, (week) => {
            _.each(week, (day) => {
                day.selected = day.date === selectedDate && day.month === selectedMonth;
            });
        });
    }

    _setCalendarWeekDays(dateObj) {
        if (dateObj) {
            this._calendar.set(dateObj);
        }
        let cal = new Calendar(this._calendar.toObject());
        this._calendarWeekDays = cal.generate();
        this._setCalendarMetaData();
        this.emit("CalendarChange", this._calendar);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    /**
     * @schema.methods.disable.name disable
     * @schema.methods.disable.description sets disabled true
     * @schema.methods.disable.returns this
     */

    disable() {
        this.setDisabled(true);
        return this;
    }

    /**
     * @schema.methods.enable.name enable
     * @schema.methods.enable.description sets disabled false
     * @schema.methods.enable.returns this
     */

    enable() {
        this.setDisabled(false);
        return this;
    }

    get view() {
        return this.getView();
    }

    set view(v) {
        this.setView(v);
    }

    /**
     * @schema.methods.getView.name getView
     * @schema.methods.getView.description get what view is currently being displayed. (days,years)
     * @schema.methods.getView.returns string
     */
    getView() {
        return this._view;
    }

    /**
     * @schema.methods.setView.name setView
     * @schema.methods.setView.description set the view to one of days or years
     * @schema.methods.setView.returns this
     * @schema.methods.setView.parameters.viewName.name viewName
     * @schema.methods.setView.parameters.viewName.description name of the view
     * @schema.methods.setView.parameters.viewName.type string
     */
    setView(v) {
        if (this._views.includes(v)) {
            this._view = v;
            this.emit("View", this._view);
        } else {
            console.warn("Date Picker - Invalid view.");
        }
        return this;
    }

    get calendarWeekDays() {
        return this.getCalendarWeekDays();
    }

    getCalendarWeekDays() {
        return this._calendarWeekDays;
    }

    get calendar() {
        return this.getCalendar();
    }

    getCalendar() {
        return this._calendar;
    }

    /**
     * @schema.methods.setValue.name setValue
     * @schema.methods.setValue.description Set the date and time value
     * @schema.methods.setValue.returns this
     * @schema.methods.setValue.parameters.newVal.name newVal
     * @schema.methods.setValue.parameters.newVal.description an instance of moment or a date object {year:2017,date:29}
     * @schema.methods.setValue.parameters.newVal.type object|moment
     */
    setValue(newVal, wasTriggeredByUser, event) {
        if (newVal && moment(newVal).isValid()) {
            let prev = this._value.clone();
            this._value.set(newVal);
            this._setCalendarWeekDays(this._value.toObject());
            this.emit("Value", this._value, prev, wasTriggeredByUser);
            if (wasTriggeredByUser) {
                this._triggerUserValue(this._value, prev, event);
            }
        }
        return this;
    }

    getValue() {
        return super.getValue();
    }

    setPreviousMonth() {
        let oldMonth = this._value.get("month");
        let newMonth = oldMonth > 0 ? oldMonth - 1 : 11;
        this.setValue({month: newMonth}, true);
    }

    setNextMonth() {
        let oldMonth = this._value.get("month");
        let newMonth = oldMonth < 11 ? oldMonth + 1 : 0;
        this.setValue({month: newMonth}, true);
    }

    /**
     * @schema.methods.showYears.name showYears
     * @schema.methods.showYears.description shows year picker
     * @schema.methods.showYears.returns this
     */

    showYears() {
        this.setView("years");
        return this;
    }

    /**
     * @schema.methods.showDays.name showDays
     * @schema.methods.showDays.description shows date picker
     * @schema.methods.showDays.returns this
     */

    showDays() {
        this.setView("days");
        return this;
    }

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        let prom = this._renderer.shutdown();
        this._renderer = undefined;
        return prom;
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = DatePickerComponent;
